import {
  type Cart,
  calculateWidthProgressbar,
  priceForShippingText,
  isDeliveryItemUnavailable,
  isPickUpItemUnavailable,
  isThirdPartyPickUpUnavailable,
  type CartEntryDetails,
  getActiveCart,
  isExceptionExist,
  ErrorResponse,
  OrderType,
  ExceptionsEnum,
  isInstallmentUnavailable,
  getStockStatus,
  getStrikeThroughPrice,
  getInstallProductDetails,
  cartApi,
  CartConstantsEnum,
  updateCart,
  getInstallmentConfig,
  type AccountProcess,
  type UpdateCartRequest,
  getVariantName,
  VariantDimensions,
  isSellableCheck,
  isGroupOrder,
  DeliveryType,
  includesAny,
  isInactiveCart,
} from '@commerce-cart-webcomponents/sdk';
import {
  useTranslation,
  Theme,
  AlertMessage,
  useNextgenRouter,
  TextWeight,
  IconStyle,
  useSession,
  Stepper,
} from '@nextgen-web-framework/all';
import { StorageService } from '@nextgen-web-framework/core';
import { InstalmentPlans } from '@commerce-payment-webcomponents/ui';
import { instalmentOrderType, Currency } from '@commerce-payment-webcomponents/sdk';
import { useDispatch } from 'react-redux';
import { type IconName } from '@fortawesome/pro-solid-svg-icons';
import { FreeProductCard } from '@commerce-product-webcomponents/ui';
import { ProductCartEntryComponent } from '@commerce-product-webcomponents/ui-composite';
import { AccountProductType } from '@commerce-cart-webcomponents/sdk/services/models/AccountProductType';
import { AboMembershipRenewal } from '../abo-membership-renewal/AboMembershipRenewal';
import { AccountRegistrationBanner } from '../../account-registration-banner/AccountRegistrationBanner';
import { ProxyOrderBanner } from '../../proxy-order-banner/ProxyOrderBanner';
import { SpecialOfferAlert } from '../special-Offer-alert/SpecialOfferAlert';
import { createDataObject } from '../../../utils';
import { CartAllSelectionCheckbox } from '../../../common/components/cart-all-selection-checkbox/CartAllSelectionCheckbox';
import { useFeatureFlag } from '../../../common';
import styles from './CartLineItemsHeader.module.scss';

export type CartLineItemsHeaderProps = {
  data: Cart;
  cartDetailsPage?: boolean;
  onEntryRemove?: (entry: CartEntryDetails) => void;
  displayGCO?: boolean;
};
const CartLineItemsHeader = ({
  data,
  cartDetailsPage = false,
  onEntryRemove,
  displayGCO,
}: CartLineItemsHeaderProps) => {
  const t = useTranslation();
  const router = useNextgenRouter();
  const dispatch = useDispatch();
  const { isABO, isAnonymous } = useSession();
  const cartId = String(router.params?.cartId);
  const mainSubCart = getActiveCart(data, cartId);
  const { installmentPlanTitleFromConfig } = getInstallmentConfig();
  const installmentProducts = getInstallProductDetails(mainSubCart.entries);
  const installmentDetails = mainSubCart?.paymentDetails?.[0];
  const { entries: cartEntries, promotionalItemOptions } = mainSubCart;
  const shippingFree = data.shippingFee?.freeShipping;
  const { showAuxiliaryToggleFlag, cartSelectionFlag } = useFeatureFlag();

  const isAuxiliaryProduct = (entry: CartEntryDetails) =>
    entry?.product?.isAuxiliary ? entry?.qualifyingItemIds?.length != 1 : true;

  const freeItems = cartEntries?.filter(
    (entry: CartEntryDetails) => entry.isFreeItem && (showAuxiliaryToggleFlag ? isAuxiliaryProduct(entry) : true),
  );

  const pwpItems = cartEntries?.filter(
    (entry: CartEntryDetails) =>
      entry.partnerPromotionId && !entry.isFreeItem && (showAuxiliaryToggleFlag ? !entry.product?.isAuxiliary : true),
  );
  const aboRenewalItem = cartEntries?.find((entry: CartEntryDetails) => includesAny(entry, [AccountProductType.Fee]));

  const isGCOPage = isGroupOrder(data.orderType) && !cartDetailsPage;

  const isRegularOrderType = data.orderType === OrderType.Regular;
  const errorData = isGCOPage ? data.errors : mainSubCart.errors;
  const allTextForCheckbox = t('cart.cartLineItemHeader.allTextForCheckbox');

  const hasShippingError =
    isPickUpItemUnavailable?.(false, mainSubCart) ||
    isDeliveryItemUnavailable?.(false, mainSubCart) ||
    isThirdPartyPickUpUnavailable?.(false, mainSubCart);

  const showFullFillment =
    !isExceptionExist(ExceptionsEnum.Exceptions.FulfillmentService, data.errors as ErrorResponse[]) &&
    !cartDetailsPage &&
    data.totalEntries !== 0 &&
    ((data?.shippingFee?.freeDeliveryAmount && data.shippingFee.freeDeliveryAmount > 0) ||
      data?.shippingFee?.freeShipping);

  const getFullfilmentAlertMessageData = (freeShipping?: boolean) => {
    if (freeShipping && data?.shippingFee?.freeDeliveryAmount) {
      return {
        headingContent: (
          <span className={styles['commerce-cart-shipping-text-free']} data-testid="free-shipping">
            <Stepper
              label={t('cart.cartLineItem.freeShippingTxt')}
              currentStep={100}
              totalSteps={100}
              className={`${styles['commerce-cart-shipping-section-bar-full']}`}
            />
          </span>
        ),
        styleClass: styles['success-green-background'],
        iconColor: Theme.SUCCESS,
      };
    } else if (freeShipping && !data?.shippingFee?.freeDeliveryAmount) {
      // when there is no thereshold to make eg. registration
      return {
        headingContent: (
          <span>
            {t('cart.cartLineitems.yourGetting')}{' '}
            <span className={styles['commerce-cart-shipping-freeShippingText']}>
              {t('cart.cartLineitems.freeShippingTxt')}
            </span>
          </span>
        ),
        styleClass: styles['success-green-background'],
        iconColor: Theme.SUCCESS,
      };
    }
    return {
      headingContent: (
        <span className={styles['commerce-cart-shipping-text-pay']}>
          <Stepper
            label={t('cart.cartLineitems.addMoreShippingTxt').replace('{{text}}', priceForShippingText(data))}
            currentStep={calculateWidthProgressbar(data)}
            totalSteps={100}
          />
        </span>
      ),
      styleClass: '',
      iconColor: Theme.ACTION,
    };
  };

  const { headingContent, iconColor, styleClass } = getFullfilmentAlertMessageData(shippingFree);

  const showAlertMessages = (
    headingText: string,
    isNotSquare?: boolean,
    iconName?: IconName,
    isWarning?: boolean,
    isMultiHeader?: boolean,
    subHeadingText?: string,
  ) => (
    <div
      className={
        isMultiHeader ? styles['commerce-purchase-limit-padding'] : styles['commerce-cart-line-item-gift-status']
      }
    >
      <AlertMessage
        content={
          isMultiHeader
            ? {
                heading: t(headingText),
                detail: t(subHeadingText as string),
              }
            : {
                heading: t(headingText),
              }
        }
        icon={isNotSquare ? iconName : 'exclamation-square'}
        textWeight={isMultiHeader ? TextWeight.MULTI : TextWeight.REGULAR}
        theme={isWarning ? Theme.WARNING : Theme.ERROR}
      />
    </div>
  );

  const updateInstallmentDetails = (planId: string, planTitle: string, paymentMethodCode?: string) => {
    const cartReqVersion = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion);
    const updatePaymentDetailsPayload = {
      orderType: data.orderType,
      version: +cartReqVersion,
      amwayUser: {
        amwayUserId: mainSubCart.amwayUser?.amwayUserId,
      },
      paymentDetails: [
        {
          paymentMethodCode: paymentMethodCode,
          paymentPlanTitle: planTitle,
          paymentPlanId: planId,
        },
      ],
    } as UpdateCartRequest;
    return cartApi.updateCart(data.id, mainSubCart?.subCartId as string, updatePaymentDetailsPayload);
  };

  const handleInstallmentPlanConfirmClick = async (planId: string, planTitle: string, paymentMethodId?: string) => {
    const res = await updateInstallmentDetails(planId, planTitle, paymentMethodId);
    dispatch(updateCart(res as Cart));
  };

  const renderAuxiliaryProducts = (entryId: string) => {
    const AuxiliaryProducts: CartEntryDetails[] = [];
    //if qualifyingItemIds length is more than 1 then it will be shown at the top
    const receivedEntries = cartEntries?.filter(
      (entry) =>
        entry?.product?.isAuxiliary &&
        entry?.qualifyingItemIds?.length === 1 &&
        entry?.qualifyingItemIds?.[0] === entryId,
    );
    receivedEntries && AuxiliaryProducts?.push(...receivedEntries);

    return (
      <>
        {Boolean(AuxiliaryProducts?.length) &&
          AuxiliaryProducts?.map((entry) => {
            const { product, totalPrice } = entry;
            return (
              <div
                key={entry.entryId}
                className={styles['commerce-cart-line-item-product']}
                data-testid="product-card-test-id"
              >
                <FreeProductCard
                  brandName={product?.brand ?? ''}
                  productName={product?.name ?? ''}
                  link={product?.cartIconPublicUrl ?? ''}
                  alt={product?.cartIconAltText ?? ''}
                  productId={''}
                  productPrice={totalPrice?.value as number}
                  currency={totalPrice?.currency as string}
                  productSize={''}
                  derivedAvailabilityStatus={getStockStatus(entry)}
                  showSubText={false}
                  productQuantity={entry?.quantity}
                  isSopBenefit={false}
                  showLinkForProductCard={!isSellableCheck(product)}
                  showDispositionHandler={false}
                  showPromotionTag={entry?.enablePromotionTag}
                  firstAvailableFutureDate={
                    product?.firstAvailableFutureDate && String(product.firstAvailableFutureDate)
                  }
                />
              </div>
            );
          })}
      </>
    );
  };

  const renderPWPItems = (pwpItems: CartEntryDetails[]) =>
    pwpItems.map((entry: CartEntryDetails, index) => {
      const { product, totalPrice } = entry;
      const dataObjectFromEntry = createDataObject(product, totalPrice, undefined, isABO());
      const isLastItem = index === pwpItems.length - 1;
      const itemClassName = isLastItem
        ? styles['commerce-cart-line-items-header-page-item']
        : styles['commerce-cart-line-items-header-page-gwp-banner-item-seperator'];

      return (
        <div key={entry.entryId} className={itemClassName}>
          <ProductCartEntryComponent
            key={entry.entryId}
            onRemoveProduct={() => onEntryRemove && onEntryRemove(entry)}
            showCartInventoryUpdate={false}
            subscriptionQtyStepperComp={
              <div className={styles['commerce-cart-line-items-header-page-item-qtyLabel']}>x {entry.quantity}</div>
            }
            showPropertiesCollection={{
              showHeartIcon: !isAnonymous() && !isSellableCheck(product),
              showRemoveIcon: true,
              showCloseCta: false,
              showQtyStepper: true,
              showPVBV: true,
              showStockStatus: true,
              disableVariantDropdown: true,
              showPromotionTag: entry?.enablePromotionTag,
            }}
            showPromotionLoader={false}
            data={{
              ...dataObjectFromEntry,
              productName: product.name ?? '',
              link: product.cartIconPublicUrl ?? '',
              alt: product.cartIconAltText as string,
              disabled: false,
              variantName: getVariantName(product.variantDimensions as VariantDimensions[]),
              strikethroughPrice: getStrikeThroughPrice(entry),
              derivedAvailabilityStatus: (!hasShippingError && getStockStatus(entry)) || '',
              firstAvailableFutureDate: product.firstAvailableFutureDate && String(product.firstAvailableFutureDate),
              showPrefix: false,
              showSubText: true,
              entryId: entry?.entryId,
              productType: product.type,
              productQuantity: entry?.quantity,
              restrictFromPdpDisplay: isSellableCheck(product),
              items: [],
            }}
            cartBaseurl={''}
          />
          <div className={styles['commerce-cart-line-items-header-page-item-auxiliary']}>
            {showAuxiliaryToggleFlag && renderAuxiliaryProducts(String(entry.entryId))}
          </div>
        </div>
      );
    });

  const renderFreeItems = (freeItems: CartEntryDetails[]) =>
    freeItems.map((entry: CartEntryDetails, index) => {
      const { product, totalPrice, sopElementId } = entry;
      const isLastItem = index === freeItems.length - 1 && pwpItems?.length === 0;

      return (
        <div
          key={entry.entryId}
          className={`${
            isLastItem
              ? styles['commerce-cart-line-items-header-page-item']
              : styles['commerce-cart-line-items-header-page-gwp-banner-item-seperator']
          }`}
        >
          <FreeProductCard
            brandName={product.brand as string}
            productName={product.name as string}
            link={product.cartIconPublicUrl as string}
            alt={product.cartIconPublicUrl as string}
            productId={product.id as string}
            productPrice={totalPrice?.value as number}
            currency={totalPrice?.currency as string}
            productSize={''}
            derivedAvailabilityStatus={getStockStatus(entry)}
            showSubText={false}
            productQuantity={entry?.quantity}
            strikethroughPrice={getStrikeThroughPrice(entry)}
            isSopBenefit={!!sopElementId}
            showLinkForProductCard={!isSellableCheck(product)}
            showDispositionHandler={false}
            showPromotionTag={entry?.enablePromotionTag}
            firstAvailableFutureDate={product?.firstAvailableFutureDate && String(product.firstAvailableFutureDate)}
          />
          <div className={styles['commerce-cart-line-items-header-page-item-auxiliary']}>
            {showAuxiliaryToggleFlag && renderAuxiliaryProducts(String(entry.entryId))}
          </div>
        </div>
      );
    });

  return (
    <div className={styles['commerce-cart-line-items-header-page']}>
      <AccountRegistrationBanner
        accountProcessDetails={data.accountProcess as AccountProcess}
        registrationFee={String(aboRenewalItem?.totalPrice?.value ?? '')}
      />

      <AboMembershipRenewal hasAccountProcess={!!data.accountProcess} cartEntries={cartEntries} />

      {isABO() && !cartDetailsPage && (
        <div className={styles['commerce-cart-line-item-gco-toggle']}>
          <ProxyOrderBanner />
        </div>
      )}

      {Boolean(installmentProducts.length) && isRegularOrderType && (
        <div className={styles['commerce-cart-line-item-gco-toggle']}>
          <InstalmentPlans
            orderTotal={{
              currency: Currency.Thb,
              value: data.totalPrice?.value,
            }}
            selectedPlanLabel={installmentDetails?.paymentPlanTitle ?? installmentPlanTitleFromConfig}
            selectedPlanId={(installmentDetails?.paymentPlanId as string) ?? ''}
            orderType={instalmentOrderType.ORDER}
            onConfirmCallBack={(planId, planTitle, paymentMethodCode) =>
              handleInstallmentPlanConfirmClick(planId, planTitle as string, paymentMethodCode)
            }
            productDetails={installmentProducts}
          />
        </div>
      )}
      {/* sop expired Error */}
      {isExceptionExist(ExceptionsEnum.Exceptions.SopElementExpired, errorData ?? []) &&
        showAlertMessages('cart.cartlineItemHeader.sopExpired')}
      {isABO() &&
        isInactiveCart(isGCOPage ? data.amwayUser : mainSubCart.amwayUser) &&
        showAlertMessages('cart.cartLineItem.inActiveCart', false, 'exclamation-square', false, false)}
      {/* Ratioinng alert Error */}
      {isExceptionExist(ExceptionsEnum.Exceptions.RationingLimitExceed, errorData ?? []) &&
        !hasShippingError &&
        showAlertMessages(
          'cart.cartLineItem.purchaseLimitExceeded',
          false,
          'exclamation-square',
          false,
          true,
          'cart.cartLineItem.updateItemLimit',
        )}
      {isExceptionExist(DeliveryType.Bopis, errorData ?? []) &&
        showAlertMessages('cart.cartLineItemsHeader.pickUnavailable')}
      {isExceptionExist(DeliveryType.Homedelivery, errorData ?? []) &&
        showAlertMessages('cart.cartLineItemsHeader.deliveryUnavailable')}
      {(isExceptionExist(DeliveryType.Kerry, errorData ?? []) ||
        isExceptionExist(DeliveryType.Seveneleven, errorData ?? [])) &&
        showAlertMessages('cart.cartLineItemsHeader.3rdPartyPickup')}

      {isExceptionExist(ExceptionsEnum.Exceptions.Restricted, errorData ?? []) &&
        showAlertMessages('cart.errorMessages.pleaseRemove')}
      {/* out of stock alert Error */}
      {isExceptionExist(ExceptionsEnum.Exceptions.ProductOutOfStock, errorData ?? []) &&
        !hasShippingError &&
        showAlertMessages('cart.cartLineItem.OOSItems')}
      {/* Free gift out of stock Warning */}
      {isExceptionExist(ExceptionsEnum.Exceptions.FreeProductOutOfStock, errorData ?? []) &&
        showAlertMessages('cart.cartLineItemHeader.freeGiftOOS', true, 'exclamation-circle', true)}

      {isInstallmentUnavailable(mainSubCart.entries) &&
        isRegularOrderType &&
        showAlertMessages(
          'cart.cartlineItemHeader.instalmentsUnavailable',
          true,
          'box-check',
          true,
          true,
          'cart.cartlineItemHeader.instalmentsUnavailableDesc',
        )}

      {/* Free shipping */}
      {showFullFillment && (
        <div className={`${styles['commerce-cart-shipping']} ${styleClass}`}>
          <div className={styles['commerce-cart-shipping-text']}>
            <AlertMessage
              icon={'truck'}
              className={styles['shipping-msg']}
              iconStyle={IconStyle.SOLID}
              iconColor={iconColor}
              theme={Theme.GRAY}
              content={{
                heading: headingContent,
              }}
              textWeight={TextWeight.MULTI}
            />
          </div>
        </div>
      )}

      {/*Special promotions */}
      {(!isGroupOrder(data.orderType) || cartDetailsPage) && (
        <div
          className={`${
            (!!freeItems?.length || !!pwpItems?.length) && styles['commerce-cart-line-items-header-page-gwp-container']
          }`}
        >
          <div className={styles['commerce-cart-line-items-header-page-gwp-bar']}>
            {(data.orderType !== OrderType.Gco || cartDetailsPage) && Boolean(promotionalItemOptions?.length) ? (
              <SpecialOfferAlert data={data} />
            ) : (
              (!!freeItems?.length || !!pwpItems?.length) && (
                <div className={styles['special-offer-alert-container']}>
                  <AlertMessage
                    className={styles['special-offer-alert']}
                    iconColor={Theme.SUCCESS}
                    icon="tags"
                    iconStyle={IconStyle.SOLID}
                  >
                    <>
                      <span className={styles['alert-title']}>{t('cart.specialOffer.alertHeading')}</span>
                    </>
                  </AlertMessage>
                </div>
              )
            )}
          </div>
          {!!freeItems?.length && (
            <div className={styles['commerce-cart-line-items-header-page-gwp-banner']}>
              {renderFreeItems(freeItems)}
            </div>
          )}
          {!!pwpItems?.length && (
            <div className={styles['commerce-cart-line-items-header-page-gwp-banner']}>{renderPWPItems(pwpItems)}</div>
          )}
        </div>
      )}

      {cartSelectionFlag && displayGCO && (
        <div className={styles['commerce-cart-line-items-header-page-select-all']}>
          <CartAllSelectionCheckbox cartData={data} text={allTextForCheckbox} />
        </div>
      )}
    </div>
  );
};

export { CartLineItemsHeader };
